import { useEffect, useState } from 'react'
import { Screen, Panel, GhostButton, PrimaryButton } from '../components/ui.jsx'
import { User } from '../components/icons.jsx'
import './identity.css'

/*
 * Identity tap-select — DB-6.4 step 2 (which specific person), resolved after
 * the QR answered "where". Lightweight attribution (name selection), not
 * device-bound biometrics. Selecting a name resolves today's project + a
 * confidence level from the Assignment record (A2). Unknown identity → Low
 * confidence → Pending Desk downstream, never a block.
 */
export default function Identity({ adapter, tenant, onIdentity, onBack }) {
  const [members, setMembers] = useState(null) // null = loading
  const [resolvingId, setResolvingId] = useState(null)
  const [entering, setEntering] = useState(false)  // name capture mode
  const [autoEntry, setAutoEntry] = useState(false) // entered silently — no list to go back to
  const [typedName, setTypedName] = useState('')

  // Quiet recovery (Flow principle: when Flow can solve a problem without
  // involving the user, it should). If the team list can't be loaded — or is
  // empty — the employee never sees a system condition; the screen simply asks
  // for their name and the check-in continues. Identity resolves at Low
  // confidence and the office matches it up via the Pending Desk.
  useEffect(() => {
    let alive = true
    adapter.listTeamMembers(tenant?.id)
      .then((m) => {
        if (!alive) return
        setMembers(m)
        if (!m || m.length === 0) { setEntering(true); setAutoEntry(true) }
      })
      .catch(() => {
        if (!alive) return
        setMembers([])
        setEntering(true)
        setAutoEntry(true)
      })
    return () => { alive = false }
  }, [adapter, tenant])

  // Project selection happens AT check-in ("Where are you working today?") —
  // assignments belong to later operational workflows, not identity. A matched
  // name is High identity confidence; nothing else is resolved here.
  function pick(member) {
    setResolvingId(member.id)
    onIdentity({
      employee: { id: member.id, name: member.name },
      project: null,
      confidence: 'High',
      assignmentId: null,
      ambiguous: false,
    })
  }

  // "My name isn't listed" → lightweight identity capture, not a dead end and
  // not a jump to project selection. The typed name continues the check-in at
  // Low confidence; the unresolved identity lands on the Pending Desk for
  // office review. Capture-class: never blocks the person from working.
  function submitTypedName() {
    const name = typedName.trim().replace(/\s+/g, ' ')
    if (!name) return
    onIdentity({
      employee: { id: null, name },   // no Employee record matched — id stays null
      project: null,                   // project inference needs a matched Assignment
      confidence: 'Low',               // routes the capture to the Pending Desk
      assignmentId: null,
      ambiguous: false,
    })
  }

  return (
    <Screen>
      <button className="link-back" onClick={onBack}>← Back</button>

      <div className="stack" style={{ marginTop: 'var(--sp-2)' }}>
        <h1 className="title-xl rise" style={{ animationDelay: '60ms' }}>
          {entering ? 'What’s your name?' : 'Who’s checking in?'}
        </h1>
        <p className="subtle rise" style={{ animationDelay: '150ms' }}>
          {entering
            ? 'Type your full name — the office will match you up.'
            : tenant?.name ? `${tenant.name} · tap your name` : 'Tap your name'}
        </p>
      </div>

      {entering ? (
        <form
          className="identity-enter rise"
          onSubmit={(e) => { e.preventDefault(); submitTypedName() }}
        >
          <input
            className="identity-input"
            type="text"
            autoComplete="name"
            autoCapitalize="words"
            placeholder="Full name"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            aria-label="Your full name"
            autoFocus
          />
          <PrimaryButton type="submit" className="btn--block" disabled={!typedName.trim()}>
            Continue
          </PrimaryButton>
          {/* Only offer the list when there is a list to go back to. */}
          {!autoEntry && (
            <GhostButton type="button" onClick={() => setEntering(false)}>
              Back to the list
            </GhostButton>
          )}
        </form>
      ) : (
      <div className="identity-list">
        {members === null ? (
          <LoadingRows />
        ) : (
          members.map((m, i) => (
            <Panel
              as="button"
              key={m.id}
              className="identity-row rise"
              style={{ animationDelay: `${180 + i * 60}ms` }}
              onClick={() => pick(m)}
              disabled={!!resolvingId}
              aria-label={`Check in as ${m.name}`}
            >
              <span className="identity-avatar"><User size={20} /></span>
              <span className="identity-name">{m.name}</span>
              {resolvingId === m.id && <span className="identity-spinner" aria-hidden="true" />}
            </Panel>
          ))
        )}

        {members !== null && members.length > 0 && (
          <GhostButton className="rise" style={{ animationDelay: '260ms' }}
                       onClick={() => setEntering(true)} disabled={!!resolvingId}>
            My name isn’t listed
          </GhostButton>
        )}
      </div>
      )}
    </Screen>
  )
}

function LoadingRows() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <div key={i} className="identity-row identity-row--skeleton" style={{ animationDelay: `${i * 90}ms` }} />
      ))}
    </>
  )
}
